import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  "https://new-backend-production.apps.konnectbd.com";

export default function useDeliverySocket(
  onOrderStatusUpdate,
  onOrderAssigned,
  onBikerLocationUpdate,
  onDirectionUpdate
) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const socketRef = useRef(null);

  const callbacksRef = useRef({
    onOrderStatusUpdate,
    onOrderAssigned,
    onBikerLocationUpdate,
    onDirectionUpdate,
  });

  useEffect(() => {
    callbacksRef.current = {
      onOrderStatusUpdate,
      onOrderAssigned,
      onBikerLocationUpdate,
      onDirectionUpdate,
    };
  }, [onOrderStatusUpdate, onOrderAssigned, onBikerLocationUpdate, onDirectionUpdate]);

  useEffect(() => {
    const token = localStorage.getItem("konnect_token");

    if (!token) {
      console.log("❌ No authentication token found for delivery socket");
      return;
    }

    if (socketRef.current?.connected) {
      console.log("⚠️ Socket already connected, skipping initialization");
      return;
    }

    console.log("🔌 Initializing delivery socket connection...");
    console.log("📍 Socket URL:", `${SOCKET_URL}/delivery`);

    socketRef.current = io(`${SOCKET_URL}/delivery`, {
      auth: {
        token: token,
      },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current.on("connect", () => {
      console.log("✅ Delivery socket connected!");
      console.log("🆔 Socket ID:", socketRef.current.id);
      setIsConnected(true);
      setConnectionError(null);
    });

    socketRef.current.on("disconnect", (reason) => {
      console.log("❌ Delivery socket disconnected");
      console.log("📋 Reason:", reason);
      setIsConnected(false);
    });

    socketRef.current.on("connect_error", (error) => {
      console.error("⚠️ Delivery socket connection error:", error.message);
      setIsConnected(false);
      setConnectionError(error.message);
    });

    socketRef.current.on("order-update", (data) => {
      console.log("📦 ORDER UPDATE EVENT");
      console.log("📦 Update type:", data.type);
      console.log("📦 Update data:", data);

      switch (data.type) {
        case 'ORDER_PICKED_UP':
          console.log("🚚 ORDER PICKED UP");
          break;

        case 'ORDER_DELIVERED':
          console.log("✅ ORDER DELIVERED");
          break;

        case 'BIKER_ASSIGNED':
          console.log("🏍️ BIKER ASSIGNED TO ORDER");
          if (callbacksRef.current.onOrderAssigned) {
            callbacksRef.current.onOrderAssigned(data);
          }
          break;

        default:
          console.log("📦 OTHER ORDER UPDATE:", data.type);
          if (callbacksRef.current.onOrderStatusUpdate) {
            callbacksRef.current.onOrderStatusUpdate(data);
          }
          break;
      }
    });

    socketRef.current.on("delivery:assigned", (data) => {
      console.log("🚀 NEW DELIVERY ASSIGNED TO KONNECT BIKER");
      console.log("📦 Assignment data:", data);
      if (callbacksRef.current.onOrderAssigned) {
        callbacksRef.current.onOrderAssigned(data);
      }
    });

    socketRef.current.on("delivery:biker-location", (data) => {
      console.log("📍 BIKER LOCATION UPDATE");
      console.log("📍 Location data:", data);
      if (callbacksRef.current.onBikerLocationUpdate) {
        callbacksRef.current.onBikerLocationUpdate(data);
      }
    });

    socketRef.current.on("order:updated", (event) => {
      console.log("📡 ORDER STATUS UPDATE");
      console.log("📡 Event:", event);
      if (event.type === 'LIVE_TRACKING_STARTED' || event.type === 'BIKER_LOCATION_UPDATE') {
        if (callbacksRef.current.onBikerLocationUpdate) {
          callbacksRef.current.onBikerLocationUpdate(event);
        }
      } else if (callbacksRef.current.onOrderStatusUpdate) {
        callbacksRef.current.onOrderStatusUpdate(event);
      }
    });

    socketRef.current.on("DIRECTION_UPDATE", (data) => {
      console.log("🛣️ DIRECTION UPDATE");
      console.log("🛣️ Direction data:", data);
      if (callbacksRef.current.onDirectionUpdate) {
        callbacksRef.current.onDirectionUpdate(data);
      }
    });

    socketRef.current.on("error", (error) => {
      console.error("⚠️ Delivery socket error:", error);
      setConnectionError(error.message);
    });

    return () => {
      console.log("🧹 Cleaning up delivery socket connection");
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  const joinDeliveryRoom = useCallback((deliveryId) => {
    if (socketRef.current?.connected) {
      console.log("🏠 Joining delivery room:", deliveryId);
      socketRef.current.emit("join-delivery-room", { deliveryId });
    }
  }, []);

  const leaveDeliveryRoom = useCallback((deliveryId) => {
    if (socketRef.current?.connected) {
      console.log("🏠 Leaving delivery room:", deliveryId);
      socketRef.current.emit("leave-delivery-room", { deliveryId });
    }
  }, []);

  return {
    isConnected,
    connectionError,
    socket: socketRef.current,
    joinDeliveryRoom,
    leaveDeliveryRoom,
  };
}
